import { useCallback } from "react";
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type { CompactSelectOption } from "newui/components/ui/InputWithLabel";

import type { ColumnConfig } from "../../SpreadsheetEditor";
import { GhostAddRow } from "../../SpreadsheetEditor";
import { ScheduleRow } from "./ScheduleRow";

/**
 * Draft shape for inline schedule-of-rates row editing. Values are keyed by
 * the column's stable `localId` (not the numeric column id) so that
 * rename/retype/reorder of columns does not orphan cell data. The numeric
 * `id` is preserved through edit so save payloads can include it for
 * existing rows; new rows get a negative local id.
 */
export interface ScheduleRowDraft {
	id: number;
	sortOrder: number;
	values: Record<string, string>;
}

export interface ScheduleRowsBodyProps {
	columns: ColumnConfig[];
	rows: ScheduleRowDraft[];
	unitOptions: CompactSelectOption[];
	/** rowId -> Set of column localIds with validation errors. */
	cellErrors?: Map<number, Set<string>>;
	onAddRow: () => void;
	onChangeCell: (rowId: number, columnLocalId: string, value: string) => void;
	onBlurCurrency: (rowId: number, columnLocalId: string) => void;
	onRemoveRow: (rowId: number) => void;
	onReorderRows: (sourceIndex: number, destIndex: number) => void;
}

/**
 * Body renderer for SpreadsheetGrid inside CompanyScheduleOfRatesSettings.
 *
 * Mirrors the structure of QuoteTable's TemplateRowsBody but renders fully
 * procurer-editable cells (text / number / currency / unit-select) against
 * a per-cell value map, since the schedule-of-rates data model persists
 * one value per (row, column).
 */
export const ScheduleRowsBody: React.FC<ScheduleRowsBodyProps> = ({
	columns,
	rows,
	unitOptions,
	cellErrors,
	onAddRow,
	onChangeCell,
	onBlurCurrency,
	onRemoveRow,
	onReorderRows,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			const sourceIndex = rows.findIndex((r) => String(r.id) === active.id);
			const destIndex = rows.findIndex((r) => String(r.id) === over.id);
			if (sourceIndex < 0 || destIndex < 0) return;
			onReorderRows(sourceIndex, destIndex);
		},
		[rows, onReorderRows]
	);

	const sortableIds = rows.map((r) => String(r.id));
	// Leading sticky # + N column cells + trailing delete cell.
	const trailingColSpan = columns.length + 2;

	return (
		<>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={sortableIds}
					strategy={verticalListSortingStrategy}
				>
					{rows.map((row, rowIndex) => (
						<ScheduleRow
							key={row.id}
							row={row}
							rowIndex={rowIndex}
							columns={columns}
							unitOptions={unitOptions}
							errorCellLocalIds={cellErrors?.get(row.id)}
							onChangeCell={onChangeCell}
							onBlurCurrency={onBlurCurrency}
							onRemoveRow={onRemoveRow}
						/>
					))}
				</SortableContext>
			</DndContext>

			<GhostAddRow
				colSpan={trailingColSpan}
				onAdd={onAddRow}
				data-testid="schedule-ghost-add-row"
			/>
		</>
	);
};

export default ScheduleRowsBody;
